import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { auditLogApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, LoadingSpinner, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';
import { formatLabel } from '../lib/formatLabel';
import { useTeamId } from '../hooks/useTeamId';

export default function AuditLogsPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const limit = 50;

  // Get user's team
  const { teamId } = useTeamId();

  // Fetch audit logs
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['audit-logs', teamId, actionFilter, entityTypeFilter, startDate, endDate, page],
    queryFn: async () => {
      const res = await auditLogApi.list(teamId!, {
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit,
        offset: (page - 1) * limit,
      });
      return res.data;
    },
    enabled: !!teamId,
  });

  // Fetch statistics
  const { data: statsData } = useQuery({
    queryKey: ['audit-logs-stats', teamId, startDate, endDate],
    queryFn: async () => {
      const res = await auditLogApi.statistics(teamId!, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      return res.data;
    },
    enabled: !!teamId,
  });

  // Export CSV
  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await auditLogApi.exportCsv(teamId!, {
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('CSV exported'),
    onError: () => toast.error('Export failed'),
  });

  // Verify integrity
  const verifyMutation = useMutation({
    mutationFn: async () => {
      const res = await auditLogApi.verifyIntegrity(teamId!);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.isValid) {
        toast.success(`Chain integrity verified. ${data.totalChecked} entries checked.`);
      } else {
        toast.error(`Chain integrity COMPROMISED at sequence ${data.firstInvalidSequence}. ${data.errors.length} error(s) found.`);
      }
    },
    onError: () => toast.error('Integrity verification failed'),
  });

  const totalPages = logsData ? Math.ceil(logsData.total / limit) : 0;

  // Collect unique actions and entity types from stats for filter dropdowns
  const actionOptions = statsData?.topActions?.map((a: any) => a.action) || [];
  const entityOptions = statsData?.entityTypeBreakdown?.map((e: any) => e.entityType) || [];

  const formatAction = (action: string) => formatLabel(action);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <FeatureGate feature="audit_logs" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Audit Logs"
          description="Immutable, hash-chained record of all compliance activities"
          actions={
            <>
              <button
                onClick={() => verifyMutation.mutate()}
                disabled={!teamId || verifyMutation.isPending}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-ocean-700 bg-ocean-50 border border-ocean-200 rounded-md hover:bg-ocean-100 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                {verifyMutation.isPending ? 'Verifying...' : 'Verify Integrity'}
              </button>
              <button
                onClick={() => exportMutation.mutate()}
                disabled={!teamId || exportMutation.isPending}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Export CSV
              </button>
            </>
          }
        />

        {/* Stats bar */}
        {statsData && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Entries</p>
              <p className="text-2xl font-semibold text-navy-900">{statsData.totalLogs?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Top Action</p>
              {(() => {
                const topAction = statsData.topActions?.[0]
                  ? formatAction(statsData.topActions[0].action)
                  : 'N/A';
                return (
                  <p
                    className="text-lg font-semibold text-navy-900 truncate"
                    title={topAction}
                  >
                    {topAction}
                  </p>
                );
              })()}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Entity Types</p>
              <p className="text-2xl font-semibold text-navy-900">{statsData.entityTypeBreakdown?.length || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Date Range</p>
              <p className="text-sm font-medium text-navy-900">
                {statsData.dateRange?.first
                  ? `${new Date(statsData.dateRange.first).toLocaleDateString()} - ${new Date(statsData.dateRange.last).toLocaleDateString()}`
                  : 'No data'}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              <option value="">All Actions</option>
              {actionOptions.map((action: string) => (
                <option key={action} value={action}>{formatAction(action)}</option>
              ))}
            </select>
            <select
              value={entityTypeFilter}
              onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              <option value="">All Entity Types</option>
              {entityOptions.map((type: string) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              placeholder="Start date"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              placeholder="End date"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : !logsData?.logs?.length ? (
            <EmptyState
              icon={ShieldCheckIcon}
              title="No audit logs found"
              description="Try adjusting your filters."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logsData.logs.map((log: any) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {log.user
                          ? `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() || log.user.email
                          : <span className="text-gray-400">System</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ocean-50 text-ocean-700">
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <span className="text-gray-400">{log.entityType}/</span>
                        <span className="font-mono text-xs">{log.entityId.slice(0, 8)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400 font-mono text-xs">
                        {log.ipAddress || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <button
                            onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                            className="text-ocean-600 hover:text-ocean-800 text-xs font-medium"
                          >
                            {expandedRow === log.id ? 'Hide' : 'View'}
                          </button>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Expanded details */}
              {expandedRow && logsData.logs.find((l: any) => l.id === expandedRow) && (
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(
                      logsData.logs.find((l: any) => l.id === expandedRow)?.details,
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, logsData?.total || 0)} of {logsData?.total?.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
